import type { AvatarProps } from '@mui/material';
import type { SyntheticEvent } from 'react';
import { Avatar } from '@mui/material';
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { ColorGenerator } from '../utils/ColorGenerator';
import { CompanyLogoHelper } from '../utils/CompanyLogoHelper';

interface CompanyLogoProps extends Omit<AvatarProps, 'src' | 'alt'> {
  companyName: string;
  companyId?: number | string;
  companyUrl?: string | null;
  alt?: string;
  lazy?: boolean; // New prop to enable lazy loading
}

const CompanyLogo = React.forwardRef<HTMLDivElement, CompanyLogoProps>(
  ({ companyName, companyId, companyUrl, alt, lazy = false, ...avatarProps }, ref) => {
    const [currentSrc, setCurrentSrc] = useState<string>('');
    const [isLoading, setIsLoading] = useState(true);
    const [isInView, setIsInView] = useState(!lazy); // If not lazy, always consider it in view
    const currentUrlIndex = useRef(0);
    const isProcessingError = useRef(false);
    const elementRef = useRef<HTMLDivElement>(null);

    // Intersection Observer for lazy loading
    useEffect(() => {
      if (!lazy || !elementRef.current) return;

      const observer = new IntersectionObserver(
        (entries) => {
          const [entry] = entries;
          if (entry.isIntersecting) {
            setIsInView(true);
            observer.disconnect();
          }
        },
        {
          root: null,
          rootMargin: '300px', // Load 300px before coming into view for smoother experience
          threshold: 0,
        },
      );

      observer.observe(elementRef.current);

      return () => observer.disconnect();
    }, [lazy]);

    // Memoize fallback URLs to prevent unnecessary recalculations
    const fallbackUrls = useMemo(
      () => CompanyLogoHelper.getFallbackUrls(companyId, companyName, companyUrl ?? undefined),
      [companyId, companyName, companyUrl],
    );

    // Memoize deterministic color based on company name
    const companyColors = useMemo(() => ColorGenerator.generateColor(companyName), [companyName]);

    // Function to preload an image and return a promise
    const preloadImage = useCallback((url: string): Promise<string> => {
      return new Promise((resolve, reject) => {
        const img = new Image();
        img.onload = () => resolve(url);
        img.onerror = () => reject(new Error(`Failed to load image: ${url}`));
        img.src = url;
      });
    }, []);

    // Function to try loading images in sequence
    const loadNextAvailableImage = useCallback(async () => {
      if (isProcessingError.current) {
        return;
      }

      setIsLoading(true);

      for (let i = currentUrlIndex.current; i < fallbackUrls.length; i++) {
        try {
          const url = await preloadImage(fallbackUrls[i]);
          currentUrlIndex.current = i;
          setCurrentSrc(url);
          setIsLoading(false);
          return;
        } catch {
          // Continue to next URL
          continue;
        }
      }

      // All URLs failed, show default avatar
      currentUrlIndex.current = fallbackUrls.length;
      setCurrentSrc('');
      setIsLoading(false);
    }, [fallbackUrls, preloadImage]);

    // Reset when company changes or when component comes into view
    useEffect(() => {
      if (!isInView) return; // Don't load if not in view (for lazy loading)

      currentUrlIndex.current = 0;
      isProcessingError.current = false;
      setCurrentSrc('');
      loadNextAvailableImage();
    }, [companyId, companyName, loadNextAvailableImage, isInView]);

    // Handle image errors by trying the next URL
    const handleImageError = useCallback(
      (_event: SyntheticEvent<HTMLImageElement, Event>) => {
        // Prevent multiple simultaneous error handling
        if (isProcessingError.current) {
          return;
        }

        isProcessingError.current = true;

        // Move to next URL and try loading
        currentUrlIndex.current += 1;

        // Use setTimeout to prevent blocking the UI and allow cleanup
        setTimeout(() => {
          isProcessingError.current = false;
          loadNextAvailableImage();
        }, 50);
      },
      [loadNextAvailableImage],
    );

    // Combine refs - use elementRef for intersection observer, forward ref for external use
    const combinedRef = useCallback(
      (node: HTMLDivElement | null) => {
        if (elementRef.current !== node) {
          (elementRef as React.MutableRefObject<HTMLDivElement | null>).current = node;
        }
        if (typeof ref === 'function') {
          ref(node);
        } else if (ref && 'current' in ref) {
          (ref as React.MutableRefObject<HTMLDivElement | null>).current = node;
        }
      },
      [ref],
    );

    // Show first letter while loading or when all images fail
    if ((isLoading && !currentSrc) || (!isLoading && !currentSrc)) {
      return (
        <Avatar
          {...avatarProps}
          className="company-logo-avatar"
          ref={combinedRef}
          alt={alt || companyName}
          sx={{
            ...avatarProps.sx,
            backgroundColor: companyColors.backgroundColor,
            color: `${companyColors.textColor} !important`,
            '& *': {
              color: `${companyColors.textColor} !important`,
            },
          }}
        >
          {companyName ? companyName.charAt(0).toUpperCase() : '?'}
        </Avatar>
      );
    }

    return (
      <Avatar
        {...avatarProps}
        className="company-logo-avatar"
        ref={combinedRef}
        src={currentSrc}
        alt={alt || companyName}
        onError={handleImageError}
        variant="square"
        sx={{
          ...avatarProps.sx,
          backgroundColor: '#ffffff', // White background for logos with transparency or non-square aspect ratios
        }}
        slotProps={{
          img: {
            sx: {
              objectFit: 'contain', // Preserve aspect ratio instead of stretching
              padding: '2px', // Small padding so logo doesn't touch edges
            },
          },
        }}
      />
    );
  },
);

CompanyLogo.displayName = 'CompanyLogo';

export default CompanyLogo;
